/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { View } from "react-native";
import Rodal from "rodal";
import "rodal/lib/rodal.css";
import Icon from "react-icons-kit";
import { ic_close } from "react-icons-kit/md/ic_close";

import { ArchiveVideo, Portal, ProprietaryPopupModal } from "UpduoLibrary";
import {
  skaleDarkGray,
  skaleWebBorderGray,
  skaleWhite,
} from "config/UpduoColors";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import { useDispatch, useSelector } from "react-redux";
import { RootState, VideoInfoType } from "store/types";
import {
  SkaleSessionType,
  useGetStaffSessionSecretQuery,
} from "graphql/internal/generated";
import { getTranscriptJson } from "config/Utilities";
import { ActionCreators } from "actions";

export function AutoLoadingVideoModal({
  sessionId,
  onClose,
  onUpdateSession,
}: {
  sessionId: string;
  onClose: () => void;
  origin?: string;
  onUpdateSession?: (
    changes: Partial<
      Pick<SkaleSessionType, "isCancelled" | "allowSharing" | "masteryEdited">
    >
  ) => void;
}) {
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const [transcript, setTranscript] = useState<any>();
  const [transcriptLoading, setTranscriptLoading] = useState<any>();

  const { data: staffSessionData, loading: getStaffSessionSecretLoading } =
    useGetStaffSessionSecretQuery({
      fetchPolicy: "no-cache",
      variables: {
        getStaffSessionSecretRequest: {
          sessionId,
        },
      },
    });

  const videoInfo = staffSessionData?.staffSession;

  useEffect(() => {
    if (
      selfObject.organizationShowTranscription &&
      videoInfo?.hasTranscript &&
      videoInfo?.transcriptionUrl &&
      videoInfo &&
      !transcript &&
      !transcriptLoading
    ) {
      setTranscriptLoading(true);
      getTranscriptJson(videoInfo)
        .then((json) => {
          setTranscript(json);
          setTranscriptLoading(false);
        })
        .catch((err) => {
          setTranscriptLoading(false);
          UpduoAnalytics.logEvent("app-video-load-transcript-err", err);
          console.error("app-video-load-transcript-err", err);
        });
    }
  }, [videoInfo, transcript, transcriptLoading]);

  if (getStaffSessionSecretLoading || transcriptLoading || !videoInfo) {
    // TODO handle loading and error
    return null;
  }

  return (
    <VideoModal
      videoInfo={
        {
          ...videoInfo,
          transcriptJson: transcript,
        } as any /*TODO transcript should not be passed in as a prop; it should be loaded inside the component*/
      }
      onClose={onClose}
      onUpdateSession={onUpdateSession}
    />
  );
}

export function VideoModal({
  videoInfo,
  onClose,
  onUpdateSession,
  origin = "",
}: {
  videoInfo: VideoInfoType;
  onClose: () => void;
  origin?: string;
  onUpdateSession?: (
    changes: Partial<
      Pick<SkaleSessionType, "isCancelled" | "allowSharing" | "masteryEdited">
    >
  ) => void;
}) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (videoInfo?.organization?.showProprietaryPopup) {
      // TODO I don't love doing it this way. I would like to move the proprietary
      // modal to be a child of the ArchiveVideo and not use redux
      dispatch(ActionCreators.setProprietaryPopupModalIsVisible(true));
    }
  }, [videoInfo]);

  return (
    <Portal>
      <Rodal
        visible={!!videoInfo}
        customStyles={{
          width: "60%",
          minWidth: 800,
          maxWidth: 1200,
          height: "80%",
          minHeight: 650,
          borderWidth: 1,
          borderRadius: 12,
          padding: 0,
          borderColor: skaleWebBorderGray,
          backgroundColor: skaleWhite,
        }}
        customMaskStyles={{ marginTop: -100, height: "250%" }}
        onClose={() => {
          UpduoAnalytics.logEvent("user-video-exit");
          onClose();
        }}
      >
        {videoInfo ? (
          <ArchiveVideo
            videoInfo={videoInfo}
            onUpdateSession={onUpdateSession}
          />
        ) : null}
        <View style={{ position: "absolute", top: 14, right: 14 }}>
          <div style={{ color: skaleDarkGray }}>
            <Icon size={20} icon={ic_close} />
          </div>
        </View>
        {videoInfo?.organization?.showProprietaryPopup ? (
          <ProprietaryPopupModal origin={origin} />
        ) : null}
      </Rodal>
    </Portal>
  );
}
